import { useForm } from '@tanstack/react-form'
import { TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { useLoginMutation } from '../hooks'
import { useResendVerificationMutation } from '../hooks/useResendVerificationMutation'
import { LoginSchema } from '../schema/loginSchema'
import type { ApiError } from '@/types/api'
import { Dialog } from '@/components/custom/dialog'
import { FormInput } from '@/components/custom/form-input'
import LoadingButton from '@/components/custom/loading-button'
import PasswordInput from '@/components/custom/password-input'
import { notifyRequestError } from '@/utils/notifyRequestError'

export function LoginForm() {
  const { mutateAsync: loginMutation, isPending } = useLoginMutation()
  const { mutateAsync: resendVerification } = useResendVerificationMutation()
  const [showVerifyDialog, setShowVerifyDialog] = useState(false)

  const form = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    onSubmit: async ({ value }) => {
      try {
        await loginMutation(value)
      } catch (error) {
        const apiError = error as ApiError
        if (apiError.status === 403) {
          setShowVerifyDialog(true)
          return
        }
        notifyRequestError(apiError, 'Login failed. Please try again.')
      }
    },
  })

  const handleResendVerification = async () => {
    try {
      await resendVerification(form.state.values.email)
      setShowVerifyDialog(false)
    } catch (error) {
      console.error('Error resending verification email', error)
    }
  }

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        className="space-y-3"
      >
        <form.Field
          name="email"
          validators={{
            onBlur: ({ value }) => {
              const result = LoginSchema.shape.email.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <div className="gap-0">
                <FormInput
                  field={field}
                  label="Email"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="off"
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              </div>
            )
          }}
        />
        <form.Field
          name="password"
          validators={{
            onBlur: ({ value }) => {
              const result = LoginSchema.shape.password.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <div className="gap-0">
                <PasswordInput
                  placeholder="••••••••"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  className="pr-10"
                  field={field}
                />
              </div>
            )
          }}
        />

        <form.Subscribe
          selector={(state) => [state.canSubmit]}
          children={([canSubmit]) => (
            <>
              <LoadingButton
                type="submit"
                className="w-full"
                isLoading={isPending}
                disabled={!canSubmit || isPending}
              >
                {isPending ? 'Signing in...' : 'Sign in'}
              </LoadingButton>
            </>
          )}
        />
      </form>
      {showVerifyDialog && (
        <Dialog
          open={showVerifyDialog}
          onClose={() => setShowVerifyDialog(false)}
          title="Email Verification Required"
          trigger={<></>}
          footer={
            <div className="flex justify-center gap-3">
              <button
                onClick={handleResendVerification}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition"
              >
                Resend Verification Email
              </button>
            </div>
          }
        >
          <div className="text-center p-6">
            <div className="flex flex-col items-center justify-center mb-4">
              <div className="p-3 rounded-full bg-yellow-100 mb-3">
                <TriangleAlert className="w-8 h-8 text-yellow-700" />
              </div>
              <h2 className="text-lg font-semibold text-yellow-800">
                Your email is not verified
              </h2>
            </div>
            <p className="text-gray-700 mb-2">
              We’ve sent a verification link to your email address.
            </p>
            <p className="text-gray-600">
              Please check your inbox (or spam folder) and click the link to
              activate your account.
            </p>
          </div>
        </Dialog>
      )}
    </>
  )
}
